import YtPlaylistIcon from "@src/app/assets/icons/youtube-playlist.svg?react";
import Link from "@src/app/components/Link";
import cx from "classnames";
import { FastAverageColor } from "fast-average-color";
import { type ReactElement, useEffect, useRef, useState } from "react";

type Props = Readonly<{
  id: string;
  thumbnailUrl: string;
  link: string;
  numberOfVideo?: number;
  title: string;
}>;

export default function VideoPlaylistCard({
  id,
  thumbnailUrl,
  link,
  numberOfVideo = 3,
  title,
}: Props): ReactElement {
  const imgRef = useRef<HTMLImageElement>(null);
  const [bgColor, setBgColor] = useState<string>("transparent");

  useEffect(() => {
    if (imgRef.current) {
      const fac = new FastAverageColor();
      fac
        .getColorAsync(imgRef.current)
        .then((color) => {
          setBgColor(color.hex);
        })
        .catch(() => {
          setBgColor("#999");
        }); // Fallback color
    }
  }, [thumbnailUrl]);

  return (
    <div
      id={`video-playlist-card-${id}`}
      data-testid="video-playlist-card"
      className="group cursor-pointer"
    >
      <Link to={link} target="_blank" rel="noreferrer">
        <div className="relative block size-auto">
          {/* Background Accent Layers */}
          <div
            style={{ background: bgColor, filter: "brightness(150%)" }}
            className={cx(
              "absolute -top-[12px] w-full h-1 scale-x-90 z-0 rounded-t-lg",
            )}
          />
          <div
            style={{ background: bgColor }}
            className={cx(
              "absolute -top-[6px] w-full h-1 scale-x-95 z-10 rounded-t-lg",
            )}
          />

          {/* Thumbnail */}
          <div
            className={cx(
              "relative min-w-[276px] lg:min-w-fit w-auto h-auto aspect-video bg-red-50 rounded-[10.5px] flex items-center justify-center mt-3 z-30 overflow-hidden",
            )}
          >
            <img
              ref={imgRef}
              src={thumbnailUrl}
              crossOrigin="anonymous"
              className="aspect-video rounded-[9px] object-cover"
              alt={title}
              onLoad={() => {}}
            />
          </div>

          {/* Playlist Info */}
          <div
            style={{ background: "rgba(0, 0, 0, 0.7)" }}
            className="absolute bottom-0 right-0 z-50 m-1 flex flex-wrap items-center gap-1 rounded-[5px] bg-inverse px-[6px] py-[3px] text-label text-primary-on-dark "
          >
            <YtPlaylistIcon fill="white" className="size-3" />
            <span className="text-xs font-bold">
              {numberOfVideo} {numberOfVideo == 1 ? "video" : "videos"}
            </span>
          </div>
        </div>

        {/* Title */}
        <p className="mt-2 text-body-1 font-medium text-primary-on-dark transition-transform group-hover:underline">
          {title}
        </p>
      </Link>
    </div>
  );
}
